import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { endpoints } from './endpoints'
import { keys } from './keys'

export type VerificationState = 'verified' | 'drift' | 'unreleased' | 'not_certified' | 'unknown'
export type FreshnessState = 'fresh' | 'stale' | 'unavailable'
export type HealthState = 'healthy' | 'degraded' | 'unhealthy' | 'unknown'
export type ScanState = 'queued' | 'running' | 'complete' | 'partial' | 'failed' | 'interrupted'

export interface VersionEvidence {
  schema_version: string
  service: string
  instance_id: string
  component_version: string
  source_repository: string
  /** Full 40-character source commit, as reported by the runtime. */
  source_commit: string
  build_id: string
  built_at: string
  capabilities: Array<{ name: string; version: string }>
}

export interface VersionResult {
  scan_id: string
  component: string
  instance_id: string
  verification_state: VerificationState
  freshness_state: FreshnessState
  health_state: HealthState
  observed_at: string
  evidence: VersionEvidence | Record<string, unknown>
  payload_sha256: string
  scan_state?: ScanState
}

export interface VersionScan {
  scan_id: string
  started_at: string
  finished_at: string | null
  scan_state: ScanState
  requested_by: string | null
}

export interface VersionsResponse {
  components: VersionResult[]
  manifest_age_seconds: number | null
}

export interface VersionScanDetail {
  scan: VersionScan
  results: VersionResult[]
}

export interface VersionScanHistory {
  scans: VersionScanDetail[]
  next_cursor?: string
}

export interface StartVersionScanResponse {
  scan_id: string
}

export function useVersions() {
  return useQuery({
    queryKey: keys.versions,
    queryFn: ({ signal }) => endpoints.versions.latest({ signal }),
  })
}

export function useVersionScan(scanId: string) {
  return useQuery({
    queryKey: keys.versionScan(scanId),
    queryFn: ({ signal }) => endpoints.versions.scan(scanId, { signal }),
    enabled: Boolean(scanId),
  })
}

export function useVersionScanHistory({ limit, cursor }: { limit?: number; cursor?: string } = {}) {
  return useQuery({
    queryKey: keys.versionScanHistory(limit, cursor),
    queryFn: ({ signal }) => endpoints.versions.history({ ...(limit === undefined ? {} : { limit }), ...(cursor === undefined ? {} : { cursor }) }, { signal }),
  })
}

export function useStartVersionScan() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => endpoints.versions.startScan(),
    // A scan replaces what every version view shows, so invalidate the whole
    // `versions` key prefix — the latest report, any open scan detail and the
    // history. Awaiting the refetch keeps the mutation pending until the new
    // data is in, so the page shows one loading state rather than briefly
    // re-rendering the pre-scan report as though it were the scan's result.
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.versions })
    },
  })
}
